// Cross-origin token handoff.
//
// The refresh token is delivered as an httpOnly cookie set by the auth
// service during login (VERP-172) — it is NEVER placed in the fragment, so
// it can't leak via the URL/history and JS/XSS can't read it. Only the
// short-lived access token rides the fragment to the target app's
// /auth/callback page. The fragment is stripped from the URL by the callback
// handler before any further navigation, and never appears in proxy access
// logs (servers do not see fragments).
//
// Fragment format:
//   <target>/auth/callback#access_token=…&to=<path>
//
// Same-origin redirects (e.g. /onboard while we are already on the auth
// host) bypass this and just hand off the route to react-router.

import { api } from './api.js';
import { storage } from './storage.js';

// The destination of a cross-origin hand-off, held in memory for the
// interstitial (AUTH-507). It carries the access token in its fragment, so
// it is never put in this app's own URL or storage beyond what login already
// holds; a reload of /handoff finds nothing and goes back to sign in.
let pendingHandoff = null;

export function getPendingHandoff() {
  return pendingHandoff;
}

export function clearPendingHandoff() {
  pendingHandoff = null;
}

const SAME_ORIGIN_PATHS = [
  '/login', '/signup', '/onboard', '/verify', '/logout',
  '/forgot-password', '/reset-password', '/invite',
];

export function handoffRedirect({ token, refreshToken, redirectTo, navigate }) {
  if (!redirectTo) {
    navigate('/onboard');
    return;
  }
  let target;
  try {
    target = new URL(redirectTo, window.location.origin);
  } catch {
    navigate('/onboard');
    return;
  }
  if (target.origin === window.location.origin) {
    // Stay client-side. /onboard is the only legitimate target on the
    // auth host today; anything else falls back to a hard navigation.
    if (SAME_ORIGIN_PATHS.some((p) => target.pathname.startsWith(p))) {
      navigate(target.pathname + target.search + target.hash);
    } else {
      window.location.href = redirectTo;
    }
    return;
  }
  // Cross-origin → SSO callback handoff via fragment.
  const callback = new URL(target.origin + '/auth/callback');
  const frag = new URLSearchParams();
  frag.set('access_token', token || '');
  // refresh_token intentionally NOT put in the fragment — it is the httpOnly
  // cookie set at login (VERP-172). refreshToken param kept for signature compat.
  void refreshToken;
  frag.set('to', target.pathname + target.search);
  callback.hash = frag.toString();
  // Through the interstitial: progress while the workspace loads, and an
  // error with a way back if it never does. The page performs the
  // navigation.
  pendingHandoff = { url: callback.toString() };
  navigate('/handoff', { replace: true });
}

// #139: someone who already has a session (the refresh cookie) and opens
// /login?next=<allowed address> goes on without the form, handed off exactly
// as after a password login. Only the next in the URL counts, never one kept
// from an earlier visit, so a plain /login (where /logout lands) always shows
// the form. The server refuses a next it would not honour after a login, and
// a signed-out browser has no cookie: both resolve false and the form shows.
export async function continueSession({ next, navigate }) {
  if (!next) return false;
  try {
    const res = await api.continueSession(next);
    if (!res?.token || !res.redirectTo) return false;
    storage.saveAuth({ token: res.token, user: res.user });
    handoffRedirect({ token: res.token, redirectTo: res.redirectTo, navigate });
    return true;
  } catch {
    return false;
  }
}
